import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { verifyResetOtp, resendResetOtp } from '../services/authService';
import Navbar from '../components/Navbar';
import './Login.css';

const VerifyResetOtp = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState('');
  const inputRefs = useRef([]);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email from location state or localStorage
    const emailFromState = location.state?.email;
    const emailFromStorage = localStorage.getItem('resetEmail');

    if (emailFromState) {
      setEmail(emailFromState);
      localStorage.setItem('resetEmail', emailFromState);
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
    } else {
      // No email found, redirect to forgot password page
      navigate('/forgot-password');
    }
  }, [location, navigate]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }

    if (!/^\d*$/.test(value)) return;

    // Clear resend message when user starts typing
    if (resendMessage) {
      setResendMessage('');
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);

    // Focus last filled input or first empty
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit code');
      setLoading(false);
      return;
    }

    try {
      const response = await verifyResetOtp(email, otpString);
      setSuccess(response.message || 'OTP verified successfully');

      // Navigate to reset password page after 1 second
      setTimeout(() => {
        navigate('/reset-password', { state: { email, otp: otpString } });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setResendMessage('');
    setResending(true);

    try {
      const response = await resendResetOtp(email);
      setResendMessage(response.message || 'A new code has been sent to your email');
      setOtp(['', '', '', '', '', '']);
      // Focus first input after a brief delay
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="login-page">
      <Navbar />

      <div className="login-wrapper">
        <div className="login-card">
          {/* Decorative elements */}
          <div className="login-decor login-decor-1"></div>
          <div className="login-decor login-decor-2"></div>

          <div className="login-header">
            <div className="login-icon">
              <i className="bi bi-shield-lock"></i>
            </div>
            <h2>Verify Reset Code</h2>
            <p>Enter the 6-digit code sent to your email</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-alert login-alert-error">
                <i className="bi bi-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="login-alert login-alert-info">
                <i className="bi bi-check-circle"></i>
                <span>{success}</span>
              </div>
            )}

            {resendMessage && (
              <div className="login-alert login-alert-info">
                <i className="bi bi-check-circle"></i>
                <span>{resendMessage}</span>
              </div>
            )}

            <div className="login-field">
              <label>
                <i className="bi bi-envelope"></i>
                Email
              </label>
              <div className="login-input-wrapper">
                <input
                  type="email"
                  value={email}
                  disabled
                  style={{ opacity: 0.7 }}
                />
              </div>
            </div>

            <div className="login-field">
              <label>
                <i className="bi bi-123"></i>
                Verification Code
              </label>
              <div className="otp-inputs" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="otp-input"
                    disabled={loading || !!success}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading || !!success}
            >
              {loading ? (
                <>
                  <span className="login-spinner"></span>
                  Verifying...
                </>
              ) : (
                <>
                  Verify Code
                  <i className="bi bi-arrow-right"></i>
                </>
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resending || loading || !!success}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#BFFF00',
                  fontWeight: 600,
                  cursor: resending || loading || !!success ? 'not-allowed' : 'pointer',
                  opacity: resending || loading || !!success ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'color 0.3s ease'
                }}
              >
                <i className="bi bi-arrow-repeat"></i>
                {resending ? 'Resending...' : 'Resend Code'}
              </button>
            </div>
          </form>

          <div className="login-divider">
            <span>or</span>
          </div>

          <div className="login-footer">
            <p>
              Remember your password? <Link to="/login">Back to Login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyResetOtp;
